/**
 * 出团通知书段落的 Markdown 渲染（网页与浏览器端 PDF 用）。
 *
 * 与后端 backend/utils/markdown.js 必须保持同一套规则，否则网页和邮件 PDF 排版不一致：
 * - gfm + breaks：单个换行保留为换行，兼容历史上按纯文本录入的内容
 * - 关闭缩进代码块：历史内容里有缩进 4 格的列表行，不能被渲染成代码块（围栏代码块 ``` 照常可用）
 * - 文字行下紧跟 ---- 时按分隔线处理，不当作 setext 二级标题
 * - 原始 HTML 一律转义，链接只放行 http/https/mailto/tel；输出再经 DOMPurify 清洗
 */
import { Marked } from 'marked';
import DOMPurify from 'dompurify';

const esc = (str) => String(str == null ? '' : str)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const SAFE_URL = /^(https?:|mailto:|tel:)/i;

const md = new Marked({ gfm: true, breaks: true, async: false });
md.use({
  tokenizer: {
    // 返回 undefined = 不产生缩进代码块 token，该行回落为普通段落
    code() { return undefined; },
  },
  renderer: {
    html(html) { return esc(html); },
    link(href, title, text) {
      if (!href || !SAFE_URL.test(href)) return text;
      const t = title ? ` title="${esc(title)}"` : '';
      return `<a href="${esc(href)}"${t} target="_blank" rel="noopener noreferrer">${text}</a>`;
    },
    image(href, title, text) {
      if (!href || !/^https?:/i.test(href)) return esc(text || '');
      const t = title ? ` title="${esc(title)}"` : '';
      return `<img src="${esc(href)}" alt="${esc(text || '')}"${t} />`;
    },
  },
});

export const preprocessMarkdown = (text) => String(text)
  .replace(/^﻿/, '')
  .replace(/\r\n?/g, '\n')
  // 非空行后紧跟 --- / === / *** / ___ 行：插入空行，按分隔线处理
  .replace(/([^\n])\n(([-=*_])\3{2,}[ \t]*)(?=\n|$)/g, '$1\n\n$2');

/** Markdown → 已清洗的 HTML 字符串 */
export const renderMarkdown = (text) => {
  if (text == null || String(text).trim() === '') return '';
  const html = md.parse(preprocessMarkdown(text));
  return DOMPurify.sanitize(html, { ADD_ATTR: ['target'] });
};
